import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useMemo, useCallback, useState, useEffect } from 'react'
import { strapiGet, strapiGetAll, strapiPost, strapiPut, strapiDelete, strapiPatch } from '@/lib/strapi'
import { queueManager } from '@/lib/sync/QueueManager'
import { syncService } from '@/lib/sync/SyncService'
import { useNetworkStore } from '@/lib/store/useNetworkStore'
import { STRAPI_BASE_URL } from '@/config/api'
import type {
  MenuCategory,
  MenuItem,
  MenuItemVariant,
  MenuModifier,
  DietaryTag,
  POSOrder,
  POSOrderItem,
  POSPayment,
  EndOfDayReport,
  ItemSalesRow,
  BookingPreOrderItem,
} from '@/lib/types/pos'
import { useAppStore } from '@/lib/store/useAppStore'
import { db } from '@/lib/db/database'
import { normalizeMenuItem, normalizeMenuItems } from '@/lib/utils/pos'
import { toast } from 'sonner'

// ── Tax class → rate helper ────────────────────────────────────────────────────
export const TAX_CLASS_RATE: Record<string, number> = {
  standard: 0.2,
  reduced: 0.1,
  zero: 0.0,
}

export const DEFAULT_DIETARY_TAGS: Array<{ name: string; icon: string; color: string; sortOrder: number }> = [
  { name: 'Milk', icon: 'Milk', color: '#2563EB', sortOrder: 10 },
  { name: 'Gluten', icon: 'Wheat', color: '#B7791F', sortOrder: 20 },
  { name: 'Sulphites', icon: 'Wine', color: '#7C3AED', sortOrder: 30 },
  { name: 'Soy', icon: 'Sprout', color: '#16A34A', sortOrder: 40 },
  { name: 'Sesame', icon: 'CircleDot', color: '#D97706', sortOrder: 50 },
  { name: 'Lupin', icon: 'Flower2', color: '#DB2777', sortOrder: 60 },
  { name: 'Nuts', icon: 'Nut', color: '#92400E', sortOrder: 70 },
]

function selectedOptionTotal(selectedOptions: unknown): number {
  if (!Array.isArray(selectedOptions)) return 0
  return selectedOptions.reduce((sum, option: any) => {
    const quantity = Math.max(1, Number(option?.quantity) || 1)
    const priceDelta = Number(option?.priceDelta ?? 0)
    return sum + (Number.isFinite(priceDelta) ? priceDelta * quantity : 0)
  }, 0)
}

// ── Menu Categories ────────────────────────────────────────────────────────────

export function usePOSMenuSync() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  const isOnline = useNetworkStore((s) => s.isOnline)
  return useQuery({
    queryKey: ['pos-menu-sync', restaurantId],
    queryFn: () => refreshMenuStores(restaurantId),
    enabled: !!restaurantId && isOnline,
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnMount: 'always',
  })
}

export function useMenuCategories() {
  const all = useLiveQuery(() => db.menuCategories.toArray(), [])
  const data = useMemo(
    () => all?.slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)),
    [all],
  )
  return { data: data !== undefined ? { data } : undefined, isLoading: data === undefined }
}

export function useCreateMenuCategory() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: (data: { name: string; description?: string; imageUrl?: string | null; sortOrder?: number }) =>
      strapiPost<MenuCategory>('/api/menu-categories', {
        data: { ...data, bookosId: `mcat-${Date.now()}`, restaurant: restaurantId },
      }),
    onSuccess: async (resp) => {
      const cat = (resp as any)?.data ?? resp
      if (cat?.documentId) await db.menuCategories.put(cat)
      toast.success('Category created')
    },
    onError: () => toast.error('Failed to create category'),
  })
}

export function useUpdateMenuCategory() {
  return useMutation({
    mutationFn: ({ documentId, ...data }: { documentId: string; name?: string; description?: string; imageUrl?: string | null; sortOrder?: number }) =>
      strapiPut<MenuCategory>(`/api/menu-categories/${documentId}`, { data }),
    onSuccess: async (resp) => {
      const cat = (resp as any)?.data ?? resp
      if (cat?.documentId) await db.menuCategories.put(cat)
      toast.success('Category updated')
    },
    onError: () => toast.error('Failed to update category'),
  })
}

type DeleteMenuCategoryInput =
  | string
  | {
      documentId: string
      reassignToCategoryDocumentId?: string | null
    }

export function useDeleteMenuCategory() {
  return useMutation({
    mutationFn: async (input: DeleteMenuCategoryInput) => {
      const documentId = typeof input === 'string' ? input : input.documentId
      const reassignToCategoryDocumentId =
        typeof input === 'string' ? null : input.reassignToCategoryDocumentId ?? null
      const itemsInCategory = await db.menuItems
        .filter((i) => i.category?.documentId === documentId)
        .toArray()

      await Promise.all(
        itemsInCategory.map((item) =>
          strapiPut(`/api/menu-items/${item.documentId}`, {
            data: { category: reassignToCategoryDocumentId },
          }),
        ),
      )
      await strapiDelete(`/api/menu-categories/${documentId}`)

      return { documentId, reassignToCategoryDocumentId, reassignedItemCount: itemsInCategory.length }
    },
    onSuccess: async ({ documentId, reassignToCategoryDocumentId, reassignedItemCount }) => {
      await db.transaction('rw', db.menuCategories, db.menuItems, async () => {
        const targetCategory = reassignToCategoryDocumentId
          ? (await db.menuCategories.get(reassignToCategoryDocumentId)) ?? null
          : null
        await db.menuCategories.delete(documentId)
        const itemsInCategory = await db.menuItems
          .filter((i) => i.category?.documentId === documentId)
          .toArray()
        await db.menuItems.bulkPut(itemsInCategory.map((item) => ({ ...item, category: targetCategory })))
      })
      toast.success(
        reassignedItemCount > 0
          ? `Category deleted and ${reassignedItemCount} item${reassignedItemCount === 1 ? '' : 's'} reassigned`
          : 'Category deleted',
      )
    },
    onError: () => toast.error('Failed to delete category'),
  })
}

export async function uploadMenuImage(file: File): Promise<string> {
  const token = (await import('@/utils/storage')).storage.getToken()
  const form = new FormData()
  form.append('files', file)

  const res = await fetch(`${STRAPI_BASE_URL}/api/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  if (!res.ok) throw new Error('Image upload failed')

  const data = await res.json()
  const imageUrl = data?.[0]?.url
  if (typeof imageUrl !== 'string' || !imageUrl.trim()) {
    throw new Error('Upload did not return an image URL')
  }
  return imageUrl
}

// Dietary / allergy tags

export function useDietaryTags() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useQuery({
    queryKey: ['dietary-tags', restaurantId],
    queryFn: () =>
      strapiGet<DietaryTag[]>('/api/pos/dietary-tags'),
    enabled: !!restaurantId,
    staleTime: 30_000,
  })
}

export function useEnsureDefaultDietaryTags() {
  const qc = useQueryClient()
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: async (existingTags: DietaryTag[] = []) => {
      if (!restaurantId) return []
      const existingNames = new Set(existingTags.map((tag) => tag.name.trim().toLowerCase()))
      const missing = DEFAULT_DIETARY_TAGS.filter((tag) => !existingNames.has(tag.name.toLowerCase()))
      const created: DietaryTag[] = []
      for (const tag of missing) {
        const response = await strapiPost<DietaryTag>('/api/pos/dietary-tags', {
          data: {
            ...tag,
            bookosId: `diet-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          },
        })
        created.push((response as any)?.data ?? response)
      }
      return created
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dietary-tags', restaurantId] })
    },
    onError: () => toast.error('Failed to create default dietary tags'),
  })
}

export function useCreateDietaryTag() {
  const qc = useQueryClient()
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: (data: { name: string; icon: string; color: string; sortOrder?: number }) =>
      strapiPost<DietaryTag>('/api/pos/dietary-tags', {
        data: {
          ...data,
          bookosId: `diet-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dietary-tags', restaurantId] })
      toast.success('Dietary tag created')
    },
    onError: () => toast.error('Failed to create dietary tag'),
  })
}

export function useUpdateDietaryTag() {
  const qc = useQueryClient()
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: ({ documentId, ...data }: { documentId: string; name?: string; icon?: string; color?: string; sortOrder?: number }) =>
      strapiPut<DietaryTag>(`/api/pos/dietary-tags/${documentId}`, { data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dietary-tags', restaurantId] })
      toast.success('Dietary tag updated')
    },
    onError: () => toast.error('Failed to update dietary tag'),
  })
}

export function useDeleteDietaryTag() {
  const qc = useQueryClient()
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: (documentId: string) => strapiDelete(`/api/pos/dietary-tags/${documentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dietary-tags', restaurantId] })
      toast.success('Dietary tag deleted')
    },
    onError: () => toast.error('Failed to delete dietary tag'),
  })
}

// ── Menu Items ─────────────────────────────────────────────────────────────────

export function useMenuItems(categoryId?: string | null) {
  const all = useLiveQuery(() => db.menuItems.toArray(), [])
  const data = useMemo(() => {
    if (all === undefined) return undefined
    let result = all.map(normalizeMenuItem).filter((i) => !i.deleted)
    if (categoryId) result = result.filter((i) => i.category?.documentId === categoryId)
    return result.slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name))
  }, [all, categoryId])
  return { data: data !== undefined ? { data } : undefined, isLoading: data === undefined }
}

async function refreshMenuStores(restaurantId: string | null) {
  if (!restaurantId) return
  const [categories, items] = await Promise.all([
    strapiGetAll<MenuCategory>('/api/menu-categories', {
      filters: { restaurant: { documentId: { $eq: restaurantId } } },
      sort: ['sortOrder:asc', 'name:asc'],
      pagination: { pageSize: 100 },
    }),
    strapiGetAll<MenuItem>('/api/menu-items', {
      filters: { restaurant: { documentId: { $eq: restaurantId } }, deleted: { $ne: true } },
      populate: {
        category: true,
        variants: true,
        modifiers: true,
        dietaryTags: true,
        restaurant: { fields: ['timezoneId'] },
      },
      sort: ['sortOrder:asc', 'name:asc'],
      pagination: { pageSize: 100 },
    }),
  ])
  await db.transaction('rw', db.menuCategories, db.menuItems, async () => {
    await db.menuCategories.clear()
    await db.menuCategories.bulkPut(categories)
    await db.menuItems.clear()
    await db.menuItems.bulkPut(normalizeMenuItems(items))
  })
}

export function useCreateMenuItem() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: (data: Partial<MenuItem> & { categoryDocumentId?: string; dietaryTagDocumentIds?: string[] }) => {
      const { categoryDocumentId, dietaryTagDocumentIds, ...rest } = data
      return strapiPost<MenuItem>('/api/menu-items', {
        data: {
          ...rest,
          bookosId: `mitem-${Date.now()}`,
          restaurant: restaurantId,
          category: categoryDocumentId ?? null,
          dietaryTags: dietaryTagDocumentIds ?? [],
        },
      })
    },
    onSuccess: async (resp) => {
      const item = normalizeMenuItem((resp as any)?.data ?? resp)
      if (item?.documentId) await refreshMenuItemInDexie(item.documentId)
      toast.success('Item created')
    },
    onError: () => toast.error('Failed to create item'),
  })
}

export function useUpdateMenuItem() {
  return useMutation({
    mutationFn: ({
      documentId,
      categoryDocumentId,
      dietaryTagDocumentIds,
      ...data
    }: { documentId: string; categoryDocumentId?: string | null; dietaryTagDocumentIds?: string[] } & Partial<MenuItem>) =>
      strapiPut<MenuItem>(`/api/menu-items/${documentId}`, {
        data: {
          ...data,
          category: categoryDocumentId !== undefined ? categoryDocumentId : undefined,
          dietaryTags: dietaryTagDocumentIds !== undefined ? dietaryTagDocumentIds : undefined,
        },
      }),
    onSuccess: async (resp) => {
      const item = normalizeMenuItem((resp as any)?.data ?? resp)
      if (item?.documentId) await refreshMenuItemInDexie(item.documentId)
      toast.success('Item updated')
    },
    onError: () => toast.error('Failed to update item'),
  })
}

export function useDeleteMenuItem() {
  return useMutation({
    mutationFn: (documentId: string) =>
      strapiPut<MenuItem>(`/api/menu-items/${documentId}`, { data: { deleted: true, isAvailable: false } }),
    onSuccess: async (_resp, documentId) => {
      await db.menuItems.delete(documentId)
      toast.success('Item deleted')
    },
    onError: () => toast.error('Failed to delete item'),
  })
}

export function useSetItemAvailability() {
  return useMutation({
    mutationFn: ({ documentId, isAvailable }: { documentId: string; isAvailable: boolean }) =>
      strapiPatch<MenuItem>(`/api/pos/menu-items/${documentId}/availability`, { isAvailable }),
    onMutate: async ({ documentId, isAvailable }) => {
      const previous = await db.menuItems.get(documentId)
      if (previous) await db.menuItems.put({ ...previous, isAvailable })
      return { previous }
    },
    onSuccess: async (resp) => {
      const item = normalizeMenuItem((resp as any)?.data ?? resp)
      if (item?.documentId) await refreshMenuItemInDexie(item.documentId)
    },
    onError: async (_err, _vars, context) => {
      if (context?.previous) await db.menuItems.put(context.previous)
      toast.error('Failed to update availability')
    },
  })
}

// ── Menu Item Variants ─────────────────────────────────────────────────────────

async function refreshMenuItemInDexie(menuItemDocumentId: string) {
  try {
    const resp = await strapiGet<MenuItem>(`/api/menu-items/${menuItemDocumentId}`, {
      populate: {
        category: true,
        variants: true,
        modifiers: true,
        dietaryTags: true,
        restaurant: { fields: ['timezoneId'] },
      },
    })
    const item = normalizeMenuItem((resp as any)?.data ?? resp)
    if (item?.documentId) await db.menuItems.put(item)
  } catch {
    // offline — Dexie will be updated on next sync
  }
}

export function useCreateMenuItemVariant() {
  return useMutation({
    mutationFn: ({ menuItemDocumentId, name, priceDelta }: { menuItemDocumentId: string; name: string; priceDelta: number }) =>
      strapiPost<MenuItemVariant>('/api/menu-item-variants', {
        data: { bookosId: `mvar-${Date.now()}`, name, priceDelta, isAvailable: true, menuItem: menuItemDocumentId },
      }),
    onSuccess: (_resp, { menuItemDocumentId }) => refreshMenuItemInDexie(menuItemDocumentId),
    onError: () => toast.error('Failed to add variant'),
  })
}

export function useDeleteMenuItemVariant() {
  return useMutation({
    mutationFn: ({ documentId, menuItemDocumentId }: { documentId: string; menuItemDocumentId: string }) =>
      strapiDelete(`/api/menu-item-variants/${documentId}`),
    onSuccess: (_resp, { menuItemDocumentId }) => refreshMenuItemInDexie(menuItemDocumentId),
    onError: () => toast.error('Failed to delete variant'),
  })
}

// ── Menu Modifiers ─────────────────────────────────────────────────────────────

export function useCreateMenuModifier() {
  return useMutation({
    mutationFn: ({ menuItemDocumentId, name, priceDelta }: { menuItemDocumentId: string; name: string; priceDelta: number }) =>
      strapiPost<MenuModifier>('/api/menu-modifiers', {
        data: { bookosId: `mmod-${Date.now()}`, name, priceDelta, menuItem: menuItemDocumentId },
      }),
    onSuccess: (_resp, { menuItemDocumentId }) => refreshMenuItemInDexie(menuItemDocumentId),
    onError: () => toast.error('Failed to add modifier'),
  })
}

export function useDeleteMenuModifier() {
  return useMutation({
    mutationFn: ({ documentId, menuItemDocumentId }: { documentId: string; menuItemDocumentId: string }) =>
      strapiDelete(`/api/menu-modifiers/${documentId}`),
    onSuccess: (_resp, { menuItemDocumentId }) => refreshMenuItemInDexie(menuItemDocumentId),
    onError: () => toast.error('Failed to delete modifier'),
  })
}

// ── POS Orders ─────────────────────────────────────────────────────────────────

export function usePOSOrders(date?: string, options?: { refetchInterval?: number }) {
  const restaurantId = useAppStore((s) => s.restaurantId)
  const isOnline = useNetworkStore((s) => s.isOnline)

  // Background server sync — keeps Dexie up-to-date while online (other terminals' changes)
  useQuery({
    queryKey: ['pos-orders-sync', restaurantId, date],
    queryFn: async () => {
      const filters: Record<string, unknown> = {
        restaurant: { documentId: { $eq: restaurantId } },
      }
      if (date) {
        filters.openedAt = {
          $gte: new Date(`${date}T00:00:00.000`).toISOString(),
          $lte: new Date(`${date}T23:59:59.999`).toISOString(),
        }
      }
      const resp = await strapiGet<POSOrder[]>('/api/pos/orders', {
        filters,
        populate: {
          table: true,
          orderItems: { populate: { menuItem: true, variant: true } },
          payments: true,
          booking: { populate: { bookingTables: { populate: { table: true } } } },
        },
        sort: ['openedAt:desc'],
        pagination: { pageSize: 200 },
      })
      const orders = (resp as any).data ?? resp
      // Merge server orders with any pending temp items/payments in Dexie.
      // Without this, a refresh before sync completes would wipe locally-added items.
      const serverDocIds = orders.map((o: POSOrder) => o.documentId)
      if (date) {
        const serverDocIdSet = new Set(serverDocIds)
        const from = new Date(`${date}T00:00:00.000`)
        const to = new Date(`${date}T23:59:59.999`)
        const staleKeys = await db.posOrders
          .filter((o) => {
            const openedAt = new Date(o.openedAt)
            return openedAt >= from &&
              openedAt <= to &&
              !(o.documentId ?? '').startsWith('temp-') &&
              !serverDocIdSet.has(o.documentId)
          })
          .primaryKeys()
        if (staleKeys.length > 0) await db.posOrders.bulkDelete(staleKeys as string[])
      }
      const existingOrders = await db.posOrders.where('documentId').anyOf(serverDocIds).toArray()
      const existingByDocId = new Map(existingOrders.map((o) => [o.documentId, o]))
      await db.posOrders.bulkPut(
        orders.map((o: POSOrder) => {
          const existing = existingByDocId.get(o.documentId)
          const tempItems    = existing?.orderItems?.filter((i) => i.documentId.startsWith('temp-')) ?? []
          const serverPaymentKeys = new Set((o.payments ?? []).map((p) => p.clientRequestId ?? p.bookosId))
          const tempPayments = existing?.payments?.filter((p) =>
            p.documentId.startsWith('temp-') && !serverPaymentKeys.has(p.clientRequestId ?? p.bookosId),
          ) ?? []
          return {
            ...o,
            tableDocId: o.table?.documentId ?? '',
            orderItems: [...(o.orderItems ?? []), ...tempItems],
            payments:   [...(o.payments   ?? []), ...tempPayments],
          }
        }),
      )
      return orders
    },
    enabled: !!restaurantId && isOnline,
    staleTime: 0,
    refetchInterval: options?.refetchInterval ?? 30_000,
  })

  // Reactive Dexie read — updates instantly for local mutations (offline-safe)
  const [dataUpdatedAt, setDataUpdatedAt] = useState(() => Date.now())
  const all = useLiveQuery(() => db.posOrders.toArray(), [])
  useEffect(() => { if (all !== undefined) setDataUpdatedAt(Date.now()) }, [all])

  const data = useMemo(() => {
    if (all === undefined) return undefined
    let orders = all
    if (date) {
      const from = new Date(`${date}T00:00:00.000`)
      const to   = new Date(`${date}T23:59:59.999`)
      orders = orders.filter((o) => { const t = new Date(o.openedAt); return t >= from && t <= to })
    }
    return orders.slice().sort((a, b) => new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime())
  }, [all, date])

  return {
    data: data !== undefined ? { data } : undefined,
    isLoading: data === undefined,
    dataUpdatedAt,
  }
}

export function usePOSOrder(documentId: string | null) {
  const order = useLiveQuery(
    () => (documentId ? db.posOrders.get(documentId) : Promise.resolve(undefined)),
    [documentId],
  )
  // Wrap to match strapiGet shape: hook.data = { data: order }
  return {
    data: order !== undefined ? { data: order ?? null } : undefined,
    isLoading: order === undefined,
  }
}

export function useCreatePOSOrder() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (data: { tableDocumentId?: string | null; covers?: number; notes?: string; bookingDocumentId?: string; serviceMode?: 'table' | 'counter' | 'takeaway' }) => {
      const serviceMode = data.serviceMode ?? (data.tableDocumentId ? 'table' : 'counter')
      const tempId = `temp-order-${Date.now()}`
      const tableRecord = data.tableDocumentId ? await db.floorTables.get(data.tableDocumentId) : null
      const tempOrder: POSOrder = {
        id: -1,
        documentId: tempId,
        bookosId: `pos-${Date.now()}`,
        status: 'open',
        covers: data.covers ?? null,
        notes: data.notes ?? '',
        openedAt: new Date().toISOString(),
        closedAt: null,
        serviceMode,
        table: tableRecord
          ? { id: tableRecord.id, documentId: tableRecord.documentId, name: tableRecord.name, seatsMax: tableRecord.seatsMax }
          : null,
        booking: data.bookingDocumentId ? { documentId: data.bookingDocumentId } : null,
        orderItems: [],
        payments: [],
        tableDocId: data.tableDocumentId ?? '',
      }
      await db.posOrders.put(tempOrder)
      await queueManager.enqueue({
        method: 'POST',
        path: '/api/pos/orders',
        body: {
          tableDocumentId: data.tableDocumentId,
          serviceMode,
          covers: data.covers,
          notes: data.notes,
          ...(data.bookingDocumentId ? { bookingDocumentId: data.bookingDocumentId } : {}),
          restaurant: restaurantId,
          clientRequestId: tempId,
        },
        tempId,
      })
      if (isOnline) syncService.processQueue(qc).catch(console.error)
      // Return { data: order } shape so (res as any)?.data?.documentId works in POSTerminal
      return { data: tempOrder }
    },
    onSuccess: () => toast.success('Order opened'),
    onError: () => toast.error('Failed to open order'),
  })
}

export function useSendToKitchen() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (orderDocumentId: string) => {
      if (orderDocumentId.startsWith('temp-')) {
        throw new Error('Sync pending - send to kitchen after the order syncs')
      }
      const order = await db.posOrders.get(orderDocumentId)
      if (!order) throw new Error('Order not found')
      const now = new Date().toISOString()
      const pendingCount = order.orderItems.filter((i) => i.status === 'pending').length
      await db.posOrders.update(orderDocumentId, {
        status: 'sent',
        orderItems: order.orderItems.map((i) =>
          i.status === 'pending' ? { ...i, status: 'sent' as const, sentToKitchenAt: now } : i,
        ),
      })
      await queueManager.enqueue({
        method: 'POST',
        path: `/api/pos/orders/${orderDocumentId}/send-to-kitchen`,
        body: {},
      })
      if (isOnline) syncService.processQueue(qc).catch(console.error)
      return pendingCount
    },
    onSuccess: (count) => {
      const n = count ?? 0
      toast.success(n > 0 ? `${n} item${n !== 1 ? 's' : ''} sent to kitchen` : 'All items already sent')
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to send to kitchen'),
  })
}

export function useVoidOrder() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (orderDocumentId: string) => {
      if (orderDocumentId.startsWith('temp-')) {
        throw new Error('Sync pending - void after the order syncs')
      }
      await db.posOrders.update(orderDocumentId, {
        status: 'voided',
        closedAt: new Date().toISOString(),
      })
      await queueManager.enqueue({
        method: 'POST',
        path: `/api/pos/orders/${orderDocumentId}/void`,
        body: {},
      })
      if (isOnline) syncService.processQueue(qc).catch(console.error)
    },
    onSuccess: () => toast.success('Order voided'),
    onError: (err: Error) => toast.error(err.message || 'Failed to void order'),
  })
}

export function useUpdateOrderStatus() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderDocumentId, status }: { orderDocumentId: string; status: string }) => {
      await db.posOrders.update(orderDocumentId, { status: status as POSOrder['status'] })
      await queueManager.enqueue({
        method: 'PATCH',
        path: `/api/pos/orders/${orderDocumentId}/status`,
        body: { status },
      })
      if (isOnline) syncService.processQueue(qc).catch(console.error)
    },
    onError: () => toast.error('Failed to update order status'),
  })
}

export function useProcessPayment() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      orderDocumentId: string
      amount: number
      tenderType: string
      tipAmount?: number
      changeGiven?: number
      gatewayRef?: string
    }) => {
      const order = await db.posOrders.get(data.orderDocumentId)
      if (!order) throw new Error('Order not found')

      const tempPaymentId = `temp-pay-${Date.now()}`
      const tempPayment: POSPayment = {
        id: -1,
        documentId: tempPaymentId,
        bookosId: `pay-${Date.now()}`,
        amount: data.amount,
        tenderType: data.tenderType as POSPayment['tenderType'],
        gatewayRef: data.gatewayRef ?? null,
        status: 'completed',
        tipAmount: data.tipAmount ?? 0,
        processedAt: new Date().toISOString(),
        clientRequestId: tempPaymentId,
      }

      // Compute new order status from totals (include tax in gross total)
      const existingPaid = order.payments
        .filter((p) => p.status === 'completed')
        .reduce((s, p) => s + p.amount, 0)
      const totalPaid = existingPaid + data.amount
      const grossTotal = order.orderItems
        .filter((i) => i.status !== 'voided')
        .reduce((s, i) => s + i.unitPrice * i.quantity * (1 + (i.taxRate ?? 0)), 0)
      const newStatus: POSOrder['status'] = totalPaid >= grossTotal ? 'paid' : 'partially_paid'
      const now = new Date().toISOString()

      await db.posOrders.update(data.orderDocumentId, {
        status: newStatus,
        payments: [...(order.payments ?? []), tempPayment],
        ...(newStatus === 'paid' ? { closedAt: now } : {}),
      })

      // When order is fully paid and linked to a booking, mark booking as 'left'
      if (newStatus === 'paid' && order.booking?.documentId) {
        const bookingDocId = order.booking.documentId
        await db.bookings.update(bookingDocId, { status: 'left' })
        await queueManager.enqueue({
          method: 'PUT',
          path: `/api/bookings/${bookingDocId}`,
          body: { data: { status: 'left' } },
        })
      }

      await queueManager.enqueue({
        method: 'POST',
        path: '/api/pos/payments',
        body: {
          orderDocumentId: data.orderDocumentId,
          amount: data.amount,
          tenderType: data.tenderType,
          tipAmount: data.tipAmount,
          changeGiven: data.changeGiven,
          gatewayRef: data.gatewayRef,
          clientRequestId: tempPaymentId,
        },
        tempId: tempPaymentId,
      })
      if (isOnline) syncService.processQueue(qc).catch(console.error)
    },
    onError: (err: Error) => toast.error(err.message || 'Payment failed'),
  })
}

export function useAddOrderItem() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      orderDocumentId: string
      menuItemDocumentId: string
      menuItemSnapshot?: MenuItem
      variantDocumentId?: string
      quantity?: number
      weightGrams?: number
      weightIsEstimate?: boolean
      unitPrice: number
      taxRate: number
      modifiers?: Record<string, unknown>
      course?: string
      notes?: string
    }) => {
      const { orderDocumentId, menuItemDocumentId, menuItemSnapshot, variantDocumentId, ...rest } = data

      // Fetch order from Dexie (with server fallback if not cached yet)
      let order = await db.posOrders.get(orderDocumentId)
      if (!order && !orderDocumentId.startsWith('temp-')) {
        try {
          const resp = await strapiGet(`/api/pos/orders/${orderDocumentId}`, {
            populate: { table: true, orderItems: { populate: { menuItem: true, variant: true } }, payments: true },
          })
          order = (resp as any)?.data ?? (resp as any)
          if (order) await db.posOrders.put({ ...order, tableDocId: order.table?.documentId ?? '' })
        } catch { /* offline */ }
      }
      if (!order) throw new Error('Order not found')

      const rawMenuItem = await db.menuItems.get(menuItemDocumentId)
      const menuItem = rawMenuItem ? normalizeMenuItem(rawMenuItem) : (menuItemSnapshot ? normalizeMenuItem(menuItemSnapshot) : undefined)
      const variant = menuItem?.variants?.find((v) => v.documentId === variantDocumentId) ?? null
      const tempItemId = `temp-item-${Date.now()}`
      const tempItem: POSOrderItem = {
        id: -1,
        documentId: tempItemId,
        quantity: rest.quantity ?? 1,
        weightGrams: rest.weightGrams ?? null,
        weightIsEstimate: rest.weightGrams != null ? rest.weightIsEstimate !== false : false,
        unitPrice: rest.unitPrice,
        taxRate: rest.taxRate,
        modifiers: rest.modifiers ?? null,
        course: (rest.course ?? 'main') as POSOrderItem['course'],
        seatNumber: null,
        notes: rest.notes ?? '',
        status: 'pending',
        sentToKitchenAt: null,
        servedAt: null,
        menuItem: menuItem ?? normalizeMenuItem({
          id: -1,
          documentId: menuItemDocumentId,
          name: '…',
          basePrice: rest.unitPrice,
        }),
        variant,
      }

      await db.posOrders.update(orderDocumentId, {
        orderItems: [...(order.orderItems ?? []), tempItem],
      })

      await queueManager.enqueue({
        method: 'POST',
        path: '/api/pos-order-items',
        body: {
          data: {
            ...rest,
            quantity: rest.quantity ?? 1,
            weightGrams: rest.weightGrams,
            weightIsEstimate: rest.weightGrams != null ? rest.weightIsEstimate !== false : false,
            order: orderDocumentId,
            menuItem: menuItemDocumentId,
            variant: variantDocumentId ?? null,
          },
        },
        tempId: tempItemId,
      })
      if (isOnline) syncService.processQueue(qc).catch(console.error)
      return { data: tempItem }
    },
    onError: () => toast.error('Failed to add item'),
  })
}

export function useRemoveOrderItem() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ documentId, orderDocumentId }: { documentId: string; orderDocumentId: string }) => {
      const order = await db.posOrders.get(orderDocumentId)
      if (!order) return
      await db.posOrders.update(orderDocumentId, {
        orderItems: order.orderItems.filter((i) => i.documentId !== documentId),
      })
      if (!documentId.startsWith('temp-')) {
        await queueManager.enqueue({ method: 'DELETE', path: `/api/pos-order-items/${documentId}` })
        if (isOnline) syncService.processQueue(qc).catch(console.error)
      } else {
        const queuedCreates = await db.syncQueue
          .filter((op) => op.tempId === documentId && op.method === 'POST' && op.path === '/api/pos-order-items')
          .primaryKeys()
        if (queuedCreates.length) await db.syncQueue.bulkDelete(queuedCreates as string[])
      }
    },
    onError: () => toast.error('Failed to remove item'),
  })
}

export function useUpdateOrderItemQty() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ documentId, quantity }: { documentId: string; quantity: number }) => {
      // Limit scan to orders opened in the last 2 days to avoid O(n×m) on large stores
      const cutoff = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
      const recentOrders = await db.posOrders
        .where('openedAt').aboveOrEqual(cutoff)
        .toArray()
      const allOrders = recentOrders.length > 0 ? recentOrders : await db.posOrders.toArray()
      const parentOrder = allOrders.find((o) => o.orderItems?.some((i) => i.documentId === documentId))
      if (!parentOrder) return
      await db.posOrders.update(parentOrder.documentId, {
        orderItems: parentOrder.orderItems.map((i) => i.documentId === documentId ? { ...i, quantity } : i),
      })
      if (documentId.startsWith('temp-')) {
        const queuedCreate = await db.syncQueue
          .filter((op) => op.tempId === documentId && op.method === 'POST' && op.path === '/api/pos-order-items')
          .first()
        if (queuedCreate) {
          const body = queuedCreate.body as any
          await db.syncQueue.update(queuedCreate.id, {
            body: { ...body, data: { ...(body?.data ?? {}), quantity } },
          })
        }
      } else {
        await queueManager.enqueue({
          method: 'PUT',
          path: `/api/pos-order-items/${documentId}`,
          body: { data: { quantity } },
        })
        if (isOnline) syncService.processQueue(qc).catch(console.error)
      }
    },
    onError: () => toast.error('Failed to update quantity'),
  })
}

export function useUpdateOrderItem() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ documentId, patch }: { documentId: string; patch: Partial<Pick<POSOrderItem, 'quantity' | 'notes' | 'weightGrams'>> }) => {
      const data: Partial<Pick<POSOrderItem, 'quantity' | 'notes' | 'weightGrams' | 'weightIsEstimate'>> = {}
      if (patch.quantity !== undefined) data.quantity = Math.max(1, patch.quantity)
      if (patch.notes !== undefined) data.notes = patch.notes
      if (patch.weightGrams != null) {
        data.weightGrams = Math.round(patch.weightGrams)
        data.weightIsEstimate = false
      }

      const isTempItem = documentId.startsWith('temp-')
      let updated = false
      await db.transaction('rw', db.posOrders, db.syncQueue, async () => {
        const cutoff = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
        const recentOrders = await db.posOrders.where('openedAt').aboveOrEqual(cutoff).toArray()
        const allOrders = recentOrders.length > 0 ? recentOrders : await db.posOrders.toArray()
        const parentOrder = allOrders.find((o) => o.orderItems?.some((i) => i.documentId === documentId))
        if (!parentOrder) return

        const nextItems = parentOrder.orderItems.map((item) => {
          if (item.documentId !== documentId) return item
          const weightGrams = data.weightGrams ?? item.weightGrams
          const unitPrice = data.weightGrams !== undefined && item.menuItem?.soldByWeight === true
            ? (Number(item.menuItem.basePrice ?? 0) + Number(item.variant?.priceDelta ?? 0)) * Number(weightGrams) / 1000
              + selectedOptionTotal(item.modifiers)
            : item.unitPrice
          return { ...item, ...data, unitPrice }
        })
        const nextItem = nextItems.find((item) => item.documentId === documentId)
        await db.posOrders.update(parentOrder.documentId, { orderItems: nextItems })
        updated = true

        if (isTempItem) {
          const queuedCreate = await db.syncQueue
            .filter((op) => op.tempId === documentId && op.method === 'POST' && op.path === '/api/pos-order-items')
            .first()
          if (queuedCreate) {
            const body = queuedCreate.body as any
            await db.syncQueue.update(queuedCreate.id, {
              body: {
                ...body,
                data: {
                  ...(body?.data ?? {}),
                  quantity: nextItem?.quantity,
                  notes: nextItem?.notes ?? '',
                  ...(data.weightGrams !== undefined ? { weightGrams: data.weightGrams, weightIsEstimate: false } : {}),
                },
              },
            })
          }
        }
      })

      if (!updated) return
      if (isTempItem) return

      await queueManager.enqueue({
        method: 'PUT',
        path: `/api/pos-order-items/${documentId}`,
        body: { data },
      })
      if (isOnline) syncService.processQueue(qc).catch(console.error)
    },
    onError: () => toast.error('Failed to update item'),
  })
}

export function useUpdateOrderItemStatus() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ documentId, status }: { documentId: string; status: string }) => {
      // Limit scan to orders opened in the last 2 days to avoid O(n×m) on large stores
      const cutoff = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
      const recentOrders = await db.posOrders
        .where('openedAt').aboveOrEqual(cutoff)
        .toArray()
      const allOrders = recentOrders.length > 0 ? recentOrders : await db.posOrders.toArray()
      const parentOrder = allOrders.find((o) => o.orderItems?.some((i) => i.documentId === documentId))
      if (!parentOrder) return
      await db.posOrders.update(parentOrder.documentId, {
        orderItems: parentOrder.orderItems.map((i) =>
          i.documentId === documentId ? { ...i, status: status as POSOrderItem['status'] } : i,
        ),
      })
      if (!documentId.startsWith('temp-')) {
        await queueManager.enqueue({
          method: 'PUT',
          path: `/api/pos-order-items/${documentId}`,
          body: { data: { status } },
        })
        if (isOnline) syncService.processQueue(qc).catch(console.error)
      }
    },
    onError: () => toast.error('Failed to update item'),
  })
}

// ── Reports ────────────────────────────────────────────────────────────────────

export function useEndOfDayReport(date: string, enabled: boolean) {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useQuery({
    queryKey: ['pos-report-eod', restaurantId, date],
    queryFn: () =>
      strapiGet<EndOfDayReport>(`/api/pos/reports/end-of-day`, { date }),
    enabled: enabled && !!restaurantId && !!date,
    staleTime: 60_000,
  })
}

export function useItemSalesReport(dateFrom: string, dateTo: string, enabled: boolean) {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useQuery({
    queryKey: ['pos-report-items', restaurantId, dateFrom, dateTo],
    queryFn: () =>
      strapiGet<ItemSalesRow[]>(`/api/pos/reports/item-sales`, { dateFrom, dateTo }),
    enabled: enabled && !!restaurantId && !!dateFrom && !!dateTo,
    staleTime: 60_000,
  })
}

export function useDailySummaryReport(dateFrom: string, dateTo: string, enabled: boolean) {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useQuery({
    queryKey: ['pos-report-daily', restaurantId, dateFrom, dateTo],
    queryFn: () =>
      strapiGet<any[]>(`/api/pos/reports/daily-summary`, { dateFrom, dateTo }),
    enabled: enabled && !!restaurantId && !!dateFrom && !!dateTo,
    staleTime: 30_000,
  })
}

// ── Day Sessions ───────────────────────────────────────────────────────────────

export function usePOSDaySession(date: string) {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useQuery({
    queryKey: ['pos-day-session', restaurantId, date],
    queryFn: () => strapiGet<{ date: string; status: 'open' | 'closed'; closedAt: string | null }>(`/api/pos/day-sessions/${date}`),
    enabled: !!restaurantId && !!date,
    staleTime: 30_000,
  })
}

export function useClosePOSDay() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (date: string) => strapiPost(`/api/pos/day-sessions/${date}/close`, {}),
    onSuccess: (_data, date) => {
      qc.invalidateQueries({ queryKey: ['pos-day-session'] })
      qc.invalidateQueries({ queryKey: ['pos-report-eod'] })
      qc.invalidateQueries({ queryKey: ['pos-report-items'] })
      qc.invalidateQueries({ queryKey: ['pos-report-daily'] })
      toast.success(`Day ${date} closed`)
    },
    onError: () => toast.error('Failed to close day'),
  })
}

export function useReopenPOSDay() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (date: string) => strapiPost(`/api/pos/day-sessions/${date}/reopen`, {}),
    onSuccess: (_data, date) => {
      qc.invalidateQueries({ queryKey: ['pos-day-session'] })
      qc.invalidateQueries({ queryKey: ['pos-report-eod'] })
      qc.invalidateQueries({ queryKey: ['pos-report-items'] })
      qc.invalidateQueries({ queryKey: ['pos-report-daily'] })
      toast.success(`Day ${date} reopened`)
    },
    onError: () => toast.error('Failed to reopen day'),
  })
}

// ── Booking Pre-Order Items ────────────────────────────────────────────────────

export function useBookingPreOrderItems(bookingDocumentId: string | null) {
  const all = useLiveQuery(() => db.preOrderItems.toArray(), [])
  const data = useMemo(
    () => all?.filter((i) => i.bookingDocumentId === bookingDocumentId),
    [all, bookingDocumentId],
  )
  return { data: data !== undefined ? { data } : undefined, isLoading: data === undefined }
}

export function useAddPreOrderItem() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()
  const triggerSync = useCallback(() => {
    if (isOnline) syncService.processQueue(qc).catch(console.error)
  }, [isOnline, qc])

  return useMutation({
    mutationFn: async (data: {
      bookingDocumentId: string
      menuItemDocumentId: string
      variantDocumentId?: string | null
      quantity?: number
      weightGrams?: number
      course?: string
      notes?: string
    }) => {
      // Look up full objects from Dexie so the temp record is displayable
      const rawMenuItem = await db.menuItems.get(data.menuItemDocumentId)
      const menuItem = rawMenuItem ? normalizeMenuItem(rawMenuItem) : undefined
      const variant = menuItem?.variants?.find((v) => v.documentId === data.variantDocumentId) ?? null
      const tempId = `temp-poi-${Date.now()}`

      const tempItem: BookingPreOrderItem = {
        id: -1,
        documentId: tempId,
        quantity: data.quantity ?? 1,
        weightGrams: data.weightGrams ?? null,
        weightIsEstimate: data.weightGrams != null,
        course: (data.course ?? 'main') as any,
        notes: data.notes ?? '',
        menuItem: menuItem ?? normalizeMenuItem({
          id: -1,
          documentId: data.menuItemDocumentId,
          name: '…',
          basePrice: 0,
        }),
        variant,
        bookingDocumentId: data.bookingDocumentId,
      }

      await db.preOrderItems.put(tempItem)

      await queueManager.enqueue({
        method: 'POST',
        path: '/api/booking-pre-order-items',
        body: {
          data: {
            booking: data.bookingDocumentId,
            menuItem: data.menuItemDocumentId,
            variant: data.variantDocumentId ?? null,
            quantity: data.quantity ?? 1,
            weightGrams: data.weightGrams,
            course: data.course ?? 'main',
            notes: data.notes ?? '',
          },
        },
        tempId,
      })
      triggerSync()
    },
    onError: () => toast.error('Failed to add item'),
  })
}

export function useRemovePreOrderItem() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ documentId }: { documentId: string; bookingDocumentId: string }) => {
      await db.preOrderItems.delete(documentId)
      if (!documentId.startsWith('temp-')) {
        await queueManager.enqueue({ method: 'DELETE', path: `/api/booking-pre-order-items/${documentId}` })
        if (isOnline) syncService.processQueue(qc).catch(console.error)
      }
    },
    onError: () => toast.error('Failed to remove item'),
  })
}

export function useUpdatePreOrderItemQty() {
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ documentId, quantity }: { documentId: string; quantity: number; bookingDocumentId: string }) => {
      await db.preOrderItems.update(documentId, { quantity })
      if (!documentId.startsWith('temp-')) {
        await queueManager.enqueue({ method: 'PUT', path: `/api/booking-pre-order-items/${documentId}`, body: { data: { quantity } } })
        if (isOnline) syncService.processQueue(qc).catch(console.error)
      }
    },
    onError: () => toast.error('Failed to update quantity'),
  })
}

// ── Booking → POS order link ───────────────────────────────────────────────────

const ACTIVE_POS_STATUSES = ['voided', 'closed', 'paid']

/** Returns matching active (non-voided/closed/paid) POS orders for a booking. */
export function useActivePOSOrderForBooking(bookingDocumentId: string | null | undefined) {
  const all = useLiveQuery(() => db.posOrders.toArray(), [])
  const data = useMemo(() => {
    if (!bookingDocumentId || all === undefined) return undefined
    const found = all.filter(
      (o) => o.booking?.documentId === bookingDocumentId && !ACTIVE_POS_STATUSES.includes(o.status),
    )
    return { data: found }
  }, [all, bookingDocumentId])
  return { data, isLoading: data === undefined }
}

/** Idempotent: finds or creates a POS order for a booking in Dexie + queues server creation. */
export function useEnsurePOSOrderForBooking() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  const isOnline = useNetworkStore((s) => s.isOnline)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ bookingDocumentId, tableDocumentId: explicitTableDocId, covers: explicitCovers, silent }: {
      bookingDocumentId: string
      tableDocumentId?: string
      covers?: number
      silent?: boolean
    }) => {
      // Return existing active order if already in Dexie
      const all = await db.posOrders.toArray()
      const existing = all.find(
        (o) => o.booking?.documentId === bookingDocumentId && !ACTIVE_POS_STATUSES.includes(o.status),
      )
      if (existing) return { data: existing }

      // Resolve tableDocumentId + covers + bookingTables from Dexie booking
      let tableDocumentId = explicitTableDocId
      let covers = explicitCovers ?? null
      const booking = await db.bookings.get(bookingDocumentId)
      if (booking) {
        if (!tableDocumentId) tableDocumentId = booking.bookingTables?.[0]?.table?.documentId
        if (covers === null) covers = booking.people ?? null
      }

      const tempId = `temp-order-${Date.now()}`
      const tableRecord = tableDocumentId ? await db.floorTables.get(tableDocumentId) : null

      // Convert pre-order items → initial POS order items
      const preOrders = await db.preOrderItems
        .filter((i) => i.bookingDocumentId === bookingDocumentId)
        .toArray()
      const preOrderAsItems: POSOrderItem[] = preOrders.map((poi, idx) => {
        const basePrice = (poi.menuItem?.basePrice ?? 0) + (poi.variant?.priceDelta ?? 0)
        const unitPrice = poi.menuItem?.soldByWeight === true && poi.weightGrams != null
          ? basePrice * Number(poi.weightGrams) / 1000 + selectedOptionTotal(poi.selectedOptions)
          : basePrice + selectedOptionTotal(poi.selectedOptions)
        const taxRate   = TAX_CLASS_RATE[poi.menuItem?.taxClass ?? 'standard'] ?? 0
        return {
          id:               -(idx + 1),
          documentId:       `temp-item-${Date.now()}-${idx}`,
          quantity:         poi.quantity,
          weightGrams:      poi.weightGrams ?? null,
          weightIsEstimate: poi.weightIsEstimate ?? (poi.weightGrams != null),
          unitPrice,
          taxRate,
          modifiers:        poi.selectedOptions ?? null,
          course:           poi.course,
          seatNumber:       null,
          notes:            poi.notes ?? '',
          status:           'pending' as const,
          sentToKitchenAt:  null,
          servedAt:         null,
          menuItem:         poi.menuItem,
          variant:          poi.variant ?? null,
        }
      })

      const tempOrder: POSOrder = {
        id: -1,
        documentId: tempId,
        bookosId: `pos-${Date.now()}`,
        status: 'open',
        covers,
        notes: '',
        openedAt: new Date().toISOString(),
        closedAt: null,
        table: tableRecord
          ? { id: tableRecord.id, documentId: tableRecord.documentId, name: tableRecord.name, seatsMax: tableRecord.seatsMax }
          : { id: -1, documentId: tableDocumentId ?? '', name: '', seatsMax: 0 },
        // Include bookingTables so POSTablesView multi-table grouping works for temp orders
        booking: {
          documentId: bookingDocumentId,
          bookingTables: (booking?.bookingTables ?? []).map((bt) => ({
            table: bt.table ? { documentId: bt.table.documentId, name: bt.table.name } : null,
          })),
        },
        orderItems: preOrderAsItems,
        payments: [],
        tableDocId: tableDocumentId,
      }
      await db.posOrders.put(tempOrder)

      // Queue a POST for each pre-order item, referencing the order's tempId in the body.
      // resolveBody in QueueManager will substitute tempId → real order documentId during sync.
      for (const item of preOrderAsItems) {
        await queueManager.enqueue({
          method: 'POST',
          path: '/api/pos-order-items',
          body: {
            data: {
              order:    tempId,
              menuItem: item.menuItem.documentId,
              variant:  item.variant?.documentId ?? null,
              quantity: item.quantity,
              weightGrams: item.weightGrams ?? null,
              weightIsEstimate: item.weightIsEstimate ?? (item.weightGrams != null),
              unitPrice: item.unitPrice,
              taxRate:  item.taxRate,
              modifiers: item.modifiers ?? null,
              course:   item.course,
              notes:    item.notes,
            },
          },
          tempId: item.documentId,
        })
      }

      if (tableDocumentId) {
        // Table known → queue standard order creation
        await queueManager.enqueue({
          method: 'POST',
          path: '/api/pos/orders',
          body: { tableDocumentId, bookingDocumentId, covers, restaurant: restaurantId },
          tempId,
        })
      } else {
        // Table unknown → use the server's idempotent ensure-order endpoint
        await queueManager.enqueue({
          method: 'POST',
          path: `/api/pos/bookings/${bookingDocumentId}/ensure-order`,
          body: {},
          tempId,
        })
      }

      if (isOnline) syncService.processQueue(qc).catch(console.error)
      if (!silent) toast.success('POS order created')
      return { data: tempOrder }
    },
    onError: (err: any) => {
      const msg = err?.message ?? 'Failed to create POS order'
      toast.error(msg)
    },
  })
}

// ── Menu AI Import ────────────────────────────────────────────────────────────

export interface ScannedItem {
  name: string
  description: string | null
  price: number | null
  orderRole?: 'standalone' | 'child_only'
  extraAppliesToCategories?: string[]
  extraAppliesToItems?: string[]
  variants?: Array<{ name: string; priceDelta?: number | null }>
  modifiers?: Array<{ name: string; priceDelta?: number | null }>
  options?: Array<{ name: string; priceDelta?: number | null }>
}

export interface ScannedCategory {
  name: string
  items: ScannedItem[]
}

export function useScanMenuPhotos() {
  return useMutation({
    mutationFn: async (images: string[]) => {
      const res = await fetch(`${STRAPI_BASE_URL}/api/pos/menu/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await import('@/utils/storage')).storage.getToken()}`,
        },
        body: JSON.stringify({ images }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error?.message ?? 'Scan failed')
      }
      const json = await res.json()
      return json.data as { categories: ScannedCategory[] }
    },
  })
}

export function useBulkImportMenu() {
  const qc = useQueryClient()
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: async (categories: ScannedCategory[]) => {
      const res = await fetch(`${STRAPI_BASE_URL}/api/pos/menu/bulk-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await import('@/utils/storage')).storage.getToken()}`,
        },
        body: JSON.stringify({ categories }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error?.message ?? 'Import failed')
      }
      return res.json()
    },
    onSuccess: async () => {
      await refreshMenuStores(restaurantId)
      qc.invalidateQueries({ queryKey: ['menu-categories'] })
      qc.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })
}
